import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import AppNavBar from './AppNavBar';
import { useModal } from '../contexts/ModalContext';

const stripePromise = loadStripe('pk_live_51RgjcqL768UDXsKVji828T2n2jt2S3m8R9CBbk87RRnMe80Oj4RTafBFOPHllj7XPCQPjF3wvlBWEYX61Lpu6P7400f5zq5TIi');

const PRICE_ID = 'price_1TO10oL768UDXsKVYE5jblCn'; // £8.99/month

const UpgradePage = () => {
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) setUserData(userDoc.data());
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [navigate]);

  const isSubscribed = userData?.subscriptionStatus === 'active' ||
    userData?.subscriptionTier === 'premium' ||
    userData?.subscriptionTier === 'premiumPlus';

  const handleCheckout = async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    setCheckoutLoading(true);

    try {
      const stripe = await stripePromise;

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: PRICE_ID,
          userId: user.uid,
          userEmail: user.email,
          successUrl: window.location.origin + '/dashboard?upgraded=true',
          cancelUrl: window.location.origin + '/upgrade',
        }),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const session = await response.json();
      const result = await stripe.redirectToCheckout({ sessionId: session.id });

      if (result.error) {
        await showAlert(result.error.message, 'Error');
      }
    } catch (error) {
      console.error('Error:', error);
      await showAlert('Something went wrong. Please try again.', 'Error');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sand-100 to-sand-200">
        <div className="text-ink-800 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="upgrade" />

      <div className="py-16 px-4">
        <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-5xl font-bold text-coral-500 mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              {isSubscribed ? 'YOUR SUBSCRIPTION' : 'GET STARTED'}
            </h1>
            <p className="text-lg text-ink-700">
              {isSubscribed ? 'You have full unlimited access to all TeAIch tools.' : 'Unlimited access to every TeAIch tool for one simple price.'}
            </p>
          </div>

          {/* Plan Card */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-xl p-8 text-center mb-8">
            <div className="inline-block bg-coral-300 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
              TEAICH UNLIMITED
            </div>

            <div className="mb-2">
              <span className="text-6xl font-bold text-ink-900">£8.99</span>
              <span className="text-ink-500 text-lg">/month</span>
            </div>
            <p className="text-coral-600 font-semibold mb-8">First month just £0.99</p>

            <div className="text-left space-y-3 mb-8">
              {[
                'Unlimited AI marking with TeAIch Assess',
                'Unlimited lesson plans with TeAIch Planner',
                'Unlimited resource generation',
                'PDF upload & download support',
                'Detailed AI feedback on every submission',
                'Save lessons and mark schemes to your library',
                'Priority email support',
              ].map((feature) => (
                <div key={feature} className="flex items-center">
                  <span className="text-coral-500 mr-3 font-bold">✓</span>
                  <span className="text-ink-700">{feature}</span>
                </div>
              ))}
            </div>

            {isSubscribed ? (
              <div className="bg-sand-50 rounded-lg p-4 mb-4">
                <p className="text-green-700 font-semibold">✅ You're subscribed — all tools unlocked</p>
                <div className="text-sm text-ink-600 mt-2 space-y-1">
                  <div>📊 Markings this month: {userData?.assessUsage || 0}</div>
                  <div>📝 Lesson plans this month: {userData?.plannerUsage || 0}</div>
                </div>
              </div>
            ) : (
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full bg-coral-300 text-white py-4 rounded-lg font-bold text-lg hover:bg-coral-500 transition duration-200 disabled:opacity-50"
              >
                {checkoutLoading ? 'Redirecting to checkout...' : 'Subscribe Now — First Month £0.99'}
              </button>
            )}
          </div>

          {/* Cancel link for subscribed users */}
          {isSubscribed && (
            <div className="text-center mb-8">
              <button
                onClick={() => navigate('/cancel-membership')}
                className="text-red-500 hover:text-red-700 underline text-sm"
              >
                Cancel Membership
              </button>
            </div>
          )}

          {/* Back */}
          <div className="text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-ink-700 hover:text-coral-500 transition-colors underline"
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl p-8 mt-10 border-4 border-ink-900 shadow-xl">
            <h3 className="text-xl font-bold text-ink-900 text-center mb-6">FREQUENTLY ASKED QUESTIONS</h3>
            <div className="space-y-4 text-left">
              {[
                {
                  q: 'How does the first month offer work?',
                  a: 'Your first month is just £0.99. After that it\'s £8.99/month, cancel anytime.'
                },
                {
                  q: 'Can I cancel anytime?',
                  a: 'Yes — you can cancel at any time from your account page. You keep access until the end of your billing period.'
                },
                {
                  q: 'What happens to my data if I cancel?',
                  a: 'Your saved lessons and mark schemes remain in your account.'
                },
                {
                  q: 'Is there a usage limit?',
                  a: 'No — your subscription gives you unlimited access to all TeAIch tools.'
                },
              ].map(({ q, a }) => (
                <div key={q}>
                  <h4 className="font-bold text-ink-900 mb-1">{q}</h4>
                  <p className="text-ink-600 text-sm">{a}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UpgradePage;